// frontend/src/utils/export-summary.ts
import i18n from "@/i18n";
import type { ProjectModelResults } from "@/store/project-form-provider";

const importXlsx = () => import("xlsx");
const importJsPdf = () => import("jspdf");
const importAutoTable = () => import("jspdf-autotable");

type AnyDict = Record<string, any>;
const isDE = () => i18n.language?.toLowerCase().startsWith("de");
const locale = () => (isDE() ? "de-DE" : "en-US");


/* ----------------------------- tiny data helpers ---------------------------- */

function arr(x: any): AnyDict[] | undefined {
  return Array.isArray(x) ? (x as AnyDict[]) : undefined;
}
function get(a: AnyDict[] | undefined, y: number, key: string): number | string | undefined {
  if (!a) return undefined;
  const row = a.find((r) => Number(r?.Year) === y);
  return row?.[key];
}
function resolveProjectName(results: any, project: any): string {
  const r: any = results || {};
  const p = project as any;
  return (
    p?.name ||
    r?.project?.name ||
    r?.meta?.project_name ||
    r?.key_metrics?.[0]?.["Project Name"] ||
    r?.key_metrics?.[0]?.["project_name"] ||
    i18n.t("common.project")
  );
}

/* ------------------------------- format helpers ---------------------------- */

function fmtInt(n: any): string {
  if (typeof n !== "number" || !isFinite(n)) return "";
  return Math.round(n).toLocaleString(locale());
}
function fmtNum(n: any, maxFrac = 2): string {
  if (typeof n !== "number" || !isFinite(n)) return "";
  return n.toLocaleString(locale(), { maximumFractionDigits: maxFrac });
}
// 1-decimal percent (always 1 place)
function fmtPct(v: any, places = 1): string {
  if (typeof v !== "number" || !isFinite(v)) return "";
  const p = Math.abs(v) <= 1 ? v * 100 : v;
  return `${p.toLocaleString(locale(), {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  })} %`;
}
function fmtDateDotted(dateLike: any): string {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return String(dateLike);
  return new Intl.DateTimeFormat(isDE() ? "de-DE" : "en-GB", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d); // DD.MM.YYYY
}
function todayDottedBerlin(): string {
  const now = new Date();
  return new Intl.DateTimeFormat(isDE() ? "de-DE" : "en-GB", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(now); // DD.MM.YYYY
}
function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [0, 0, 0];
}

function formatShort(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (a >= 1_000) return `${Math.round(n / 1_000)}k`;
  return Math.round(n).toString();
}

/* ------------------------- content section resolvers ------------------------ */

function resolveKeyFacts(results: any, project: any): Array<[string, string]> {
  const p: any = project || {};

  const items: Array<[string, string]> = [
    [i18n.t("project.general.address"), String(p.address ?? "")],
    [i18n.t("project.general.city"), String(p.city ?? "")],
    [i18n.t("project.general.zip"), String(p.zip ?? p.postal_code ?? "")],
    [i18n.t("project.general.federal_state"), String(p.federal_state ?? p.region ?? "")],
    [i18n.t("project.general.country"), String(p.country ?? "")],
    [i18n.t("project.general.technology"), String(p.technology ?? "")],
    [i18n.t("project.general.type"), String(p.type ?? "")],
    [i18n.t("project.general.phase"), String(p.phase ?? "")],
  ];

  const typeRem = p.sales_type ?? "";
  const firstTariff = p.initial_tariff ?? "";
  const tariffDuration = p.duration_initial_tariff ?? "";

  items.push([i18n.t("project.sales.initial_tariff_type"), String(typeRem ?? "")]);
  items.push([
    i18n.t("project.sales.initial_tariff"),
    typeof firstTariff === "number"
      ? `${firstTariff.toLocaleString(locale(), { maximumFractionDigits: 2 })} ${i18n.t("project.sales.initial_tariff_unit", { defaultValue: "ct/kWh" })}`
      : String(firstTariff ?? ""),
  ]);
  items.push([
    i18n.t("project.sales.initial_tariff_duration"),
    typeof tariffDuration === "number" ? `${tariffDuration} ${i18n.t("common.years")}` : String(tariffDuration ?? ""),
  ]);

  if (typeof p.capacity === "number") items.push([i18n.t("project.production.capacity"), `${p.capacity.toLocaleString(locale())} kWp`]);
  else if (p.capacity) items.push([i18n.t("project.production.capacity"), String(p.capacity)]);

  if (p.specific_yield != null) {
    const v = typeof p.specific_yield === "number"
      ? `${p.specific_yield.toLocaleString(locale())} kWh/kWp`
      : String(p.specific_yield);
    items.push([i18n.t("project.production.specific_yield"), v]);
  }

  if (p.relative_costs != null) {
    const v = typeof p.relative_costs === "number"
      ? `${p.relative_costs.toLocaleString(locale())} ${i18n.t("project.production.relative_costs_unit", { defaultValue: "EUR/kWp" })}`
      : String(p.relative_costs);
    items.push([i18n.t("project.production.relative_costs"), v]);
  }

  if (p.dc_commissioning_date) items.push([i18n.t("project.production.dc_commissioning"), fmtDateDotted(p.dc_commissioning_date)]);
  if (p.ac_commissioning_date) items.push([i18n.t("project.production.ac_commissioning"), fmtDateDotted(p.ac_commissioning_date)]);

  return items;
}

function resolveProfitability(results: any, project: any): {
  roi20y?: number; roiLifetime?: number; paybackYears?: number;
  irrProject?: number; irrEquityPre?: number; irrEquityPost?: number;
} {
  const p: any = project || {};
  const km: AnyDict | undefined = (results as any)?.key_metrics?.[0];

  return {
    roi20y: p.roi_avg_20y,
    roiLifetime: p.roi_avg_lifetime,
    paybackYears: p.payback_period,
    irrProject: p.irr_project_unlevered_pre_tax ?? km?.["Project IRR (unlevered, pre-tax)"],
    irrEquityPre: p.irr_equity_levered_pre_tax ?? km?.["Equity IRR (levered, pre-tax)"],
    irrEquityPost: p.irr_equity_levered_post_tax ?? km?.["Equity IRR (levered, post-tax)"],
  };
}

function resolveInvestmentAndFinancing(
  results: any,
  project: any
): { totalInvestment: number; nominalDebt?: number; disagio?: number; loanProceeds?: number; equity?: number; interestRate?: number } {
  const p: any = project || {};
  const investmentResults: any[] = (results as any)?.investment ?? [];
  const initialInvestmentFromBackendRaw: number =
    investmentResults?.length > 0 ? (investmentResults[0]?.["Investment (EUR)"] as number) ?? 0 : 0;
  const totalInvestment = Math.abs(initialInvestmentFromBackendRaw);

  const debtShare = p.debt_ratio ?? 0;
  const brokerCommission = p.broker_commission ?? 0;
  const disagioRate = p.debt_disagio ?? 0;
  const nominalFromCalc = totalInvestment * (1 - brokerCommission) * debtShare;
  const disagioFromCalc = nominalFromCalc * disagioRate;
  const loanProceedsFromCalc = nominalFromCalc - disagioFromCalc;
  const equity = totalInvestment - loanProceedsFromCalc;

  const debtRows: any[] = (results as any)?.debt ?? [];
  const netDebtRows: any[] = (results as any)?.net_debt ?? [];
  const disagioRows: any[] = (results as any)?.disagio ?? [];
  const drawYearDebt = debtRows.find((row) => (row["Debt (EUR)"] ?? 0) > 0);
  const drawYearNetDebt = netDebtRows.find((row) => (row["Net Debt (EUR)"] ?? 0) > 0);
  const drawYearDisagio = disagioRows.find((row) => (row["Disagio (EUR)"] ?? 0) > 0);
  const nominalDebt = drawYearDebt?.["Debt (EUR)"] ?? nominalFromCalc;
  const loanProceeds = drawYearNetDebt?.["Net Debt (EUR)"] ?? loanProceedsFromCalc;
  const disagio = drawYearDisagio?.["Disagio (EUR)"] ?? disagioFromCalc;
  const interestRate = p.interest_rate;

  return { totalInvestment, nominalDebt, disagio, loanProceeds, equity, interestRate };
}

function resolveTaxSavingsFirst2Years(project: any, results: any) {
  const p: any = project || {};
  const taxRate = p.tax_rate ?? 0;
  const iabRate = p.investment_deduction_amount_iab ?? 0;

  const s1 = p.special_depreciation_year_1 ?? 0;
  const s2 = p.special_depreciation_year_2 ?? 0;
  const s3 = p.special_depreciation_year_3 ?? 0;
  const s4 = p.special_depreciation_year_4 ?? 0;
  const s5 = p.special_depreciation_year_5 ?? 0;
  const specialDepTotalRate = s1 + s2 + s3 + s4 + s5;

  const depreciationPeriod = p.depreciation_period ?? 0;

  const invRows: any[] = (results as any)?.investment ?? [];
  const invY1 = invRows?.length ? (invRows[0]?.["Investment (EUR)"] as number) ?? 0 : 0;
  const totalInvestment = Math.abs(invY1);

  const iabTax = totalInvestment * iabRate * taxRate;
  const specialTax2y = totalInvestment * (1 - iabRate) * (s1 + s2) * taxRate;
  const residualBase = totalInvestment * (1 - iabRate) * (1 - specialDepTotalRate);
  const basePerYearTax = depreciationPeriod > 0 ? (residualBase / depreciationPeriod) * taxRate : 0;
  const baseTax2y = 2 * basePerYearTax;
  const total = iabTax + specialTax2y + baseTax2y;

  return { iabTax, specialTax2y, baseTax2y, total };
}

/* --------------------------- chart data helpers ---------------------------- */

type CFSeries = {
  years: number[];
  preTax: number[];
  postTax: number[];
  cumulative: number[];
};

function extractCashFlowSeries(results: any): CFSeries {
  const cash = arr((results as any)?.cashflow) || [];
  const years = Array.from(new Set(cash.map((r) => Number(r?.Year)))).sort((a, b) => a - b);

  const pickNum = (y: number, key: string): number => {
    const row = cash.find((r) => Number(r?.Year) === y);
    const v = row?.[key];
    return typeof v === "number" && isFinite(v) ? v : 0;
    };

  const preKey = "Cash Flow (levered, pre-tax) (EUR)";
  const postKey = "Cash Flow (EUR)";
  const cumKey = "Cumulative Cash Flow (EUR)";

  const pre = years.map((y) => pickNum(y, preKey));
  const post = years.map((y) => pickNum(y, postKey));
  const cum = years.map((y) => pickNum(y, cumKey));

  return { years, preTax: pre, postTax: post, cumulative: cum };
}

/* ------------------------------ chart drawing ------------------------------ */

const COLOR_BLUE = "#002E66";
const COLOR_LIGHT = "#0A78FF";
const COLOR_GRID = "#d0d4d9";

type Box = { x: number; y: number; w: number; h: number };

function niceTicks(min: number, max: number, count = 5): { ticks: number[]; niceMin: number; niceMax: number } {
  if (!isFinite(min) || !isFinite(max) || min === max) {
    const v = isFinite(min) ? min : 0;
    return { ticks: [v - 1, v - 0.5, v, v + 0.5, v + 1], niceMin: v - 1, niceMax: v + 1 };
  }
  const span = max - min;
  const step0 = span / Math.max(1, count);
  const pow10 = Math.pow(10, Math.floor(Math.log10(step0)));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow10);
  const step = candidates.reduce((best, m) =>
    Math.abs(m - step0) < Math.abs(best - step0) ? m : best, candidates[0]);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) ticks.push(v);
  return { ticks, niceMin, niceMax };
}

function drawAxesAndGrid(
  doc: any,
  box: { x: number; y: number; w: number; h: number },
  minVal: number,
  maxVal: number,
  tickCount = 6
) {
  const { x, y, w, h } = box;
  let innerPadL = 16;
  const innerPad = 16;
  let gx = x + innerPadL;
  const gy = y + innerPad;
  const gh = h - innerPad * 2;

  const { ticks, niceMin, niceMax } = niceTicks(minVal, maxVal, tickCount - 1);
  const range = Math.max(1e-9, niceMax - niceMin);

  // ensure y-axis labels fit within the chart box
  let maxLblW = 0;
  for (const t of ticks) {
    const lbl = formatShort(t);
    const wlbl = doc.getTextWidth(lbl);
    if (wlbl > maxLblW) maxLblW = wlbl;
  }
  const minLabelX = gx - 6 - maxLblW;
  if (minLabelX < x) {
    const shift = x - minLabelX;
    innerPadL += shift;
    gx = x + innerPadL;
  }
  const gw = w - innerPadL - innerPad;

  // grid + ticks + labels (short form)
  const [gr, gg, gb] = hexToRgb(COLOR_GRID);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  for (const t of ticks) {
    const ty = gy + gh - ((t - niceMin) / range) * gh;

    doc.setDrawColor(gr, gg, gb);  // very light horizontal grid
    doc.setLineWidth(0.3);
    doc.line(gx, ty, gx + gw, ty);

    doc.setDrawColor(0, 0, 0);     // y-axis tick
    doc.setLineWidth(0.6);
    doc.line(gx - 3, ty, gx, ty);

    const lbl = formatShort(t);    // 20k, 2M, etc.
    doc.setTextColor(0, 0, 0);
    doc.text(lbl, gx - 6 - doc.getTextWidth(lbl), ty + 2);
  }

  // draw axes last so they sit on top of the horizontal grid
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.6);
  doc.line(gx, gy, gx, gy + gh);           // Y-axis
  doc.line(gx, gy + gh, gx + gw, gy + gh); // X-axis

  return { gx, gy, gw, gh, niceMin, range };
}

function legendWidth(doc: any, items: Array<{ label: string }>, swatch = 10, gap = 6, pad = 6) {
  let w = 0;
  for (const it of items) w += swatch + gap + doc.getTextWidth(it.label) + pad;
  return w;
}

function drawLegend(
  doc: any,
  items: Array<{ color: string; label: string }>,
  x: number,
  y: number,
  alignRight = false,
  width?: number,
  fontSize = 7 // smaller legend font
) {
  const size = 6, gap = 6, pad = 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);

  let cx = x;
  if (alignRight && width) {
    let w = 0;
    for (const it of items) w += size + gap + doc.getTextWidth(it.label) + pad;
    cx = x + width - w;
  }  

  for (const it of items) {
    const [r, g, b] = hexToRgb(it.color);
    doc.setFillColor(r, g, b);
    doc.rect(cx, y - size + 2, size, size, "F");
    cx += size + gap;
    doc.setTextColor(0, 0, 0);
    doc.text(it.label, cx, y + 2);
    cx += doc.getTextWidth(it.label) + pad;
  }
}

function drawBarChart(
  doc: any,
  box: Box,
  series: CFSeries
) {
  const { years, preTax, postTax } = series;

  const values = [...preTax, ...postTax];
  const maxVal = Math.max(...values, 0);
  const minVal = Math.min(...values, 0);

  // Axes + grid + scaling
  const layout = drawAxesAndGrid(doc, box, minVal, maxVal, 7);
  const { gx, gy, gw, gh, niceMin, range } = layout;

  // bars
  const groups = Math.max(1, years.length);
  const groupW = gw / groups;

  // target layout: [outer gap] [bar1][small intra gap][bar2] [outer gap]
  const OUTER_GAP = Math.max(2, Math.min(4, groupW * 0.05));  // small, consistent spacing between years
  const INTRA_GAP = Math.max(0.5, Math.min(1, groupW * 0.01));  // tiny gap between the two bars of one year

  const available = Math.max(4, groupW - 2 * OUTER_GAP);
  let barW = (available - INTRA_GAP) / 2;

  // clamp bar width to sensible bounds
  barW = Math.max(6, Math.min(28, barW));

  // recompute available totals in case of clamping
  const barsTotal = 2 * barW + INTRA_GAP;
  const used = barsTotal + 2 * OUTER_GAP;
  // if we somehow exceed the group width, proportionally scale both bars
  if (used > groupW) {
    const scale = (groupW - 2 * OUTER_GAP - INTRA_GAP) / (2 * barW);
    barW *= Math.max(0.7, Math.min(1, scale));
  }


  const [r1, g1, b1] = hexToRgb(COLOR_LIGHT);
  const [r2, g2, b2] = hexToRgb(COLOR_BLUE);

  for (let i = 0; i < years.length; i++) {
    const leftEdge = gx + i * groupW + OUTER_GAP;
    const xBar1 = leftEdge;
    const xBar2 = leftEdge + barW + INTRA_GAP;

    const drawBar = (val: number, x0: number, r: number, g: number, b: number) => {
      const vHeight = Math.abs(((val - 0) / range) * gh);
      const zeroY = gy + gh - ((0 - niceMin) / range) * gh;
      const top = val >= 0 ? zeroY - vHeight : zeroY;
      doc.setFillColor(r, g, b);
      // subtle rounding; never exaggerated
      const radius = Math.min(0.5, barW / 2, vHeight / 2);
      const anyDoc: any = doc;
      if (typeof anyDoc.roundedRect === "function" && radius > 0) {
        anyDoc.roundedRect(x0, top, barW, vHeight, radius, radius, "F");
      } else {
        doc.rect(x0, top, barW, vHeight, "F");
      }
    };

    drawBar(preTax[i] ?? 0, xBar1, r1, g1, b1);
    drawBar(postTax[i] ?? 0, xBar2, r2, g2, b2);
  }

  // black zero line if 0 is within the visible range
  if (niceMin < 0 && 0 < range + niceMin) {
    const zeroY = gy + gh - ((0 - niceMin) / range) * gh;
    doc.setDrawColor(128, 128, 128);
    doc.setLineWidth(0.6);
    doc.line(gx, zeroY, gx + gw, zeroY);
  }

  // x labels
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  for (let i = 0; i < years.length; i++) {
    const cx = gx + i * groupW + groupW / 2;
    doc.text(String(years[i]), cx, gy + gh + 12, { align: "center" });
  }

  // re-draw axes on top so the x-axis isn’t visually “under” bars
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.6);
  doc.line(gx, gy, gx, gy + gh);           // Y-axis
  doc.line(gx, gy + gh, gx + gw, gy + gh); // X-axis

  // x-axis legend
  doc.setFontSize(9);
  doc.text(i18n.t("project.financial_model.year_axis", { defaultValue: "Years" }), gx + gw / 2, gy + gh + 26, { align: "center" });

  // legend top-right
  drawLegend(
    doc,
    [
      { color: COLOR_LIGHT, label: i18n.t("charts.cashflow.series.cf_equity_pre_tax") },
      { color: COLOR_BLUE, label: i18n.t("charts.cashflow.series.cf_equity_post_tax") },
    ],
    gx,
    gy - 10,
    true,   // alignRight
    gw,     // width to align within
    7       // small legend font
  );
}

function drawLineChart(
  doc: any,
  box: Box,
  series: CFSeries
) {
  const { years, cumulative } = series;

  const maxVal = Math.max(...cumulative, 0);
  const minVal = Math.min(...cumulative, 0);

  const layout = drawAxesAndGrid(doc, box, minVal, maxVal, 7);
  const { gx, gy, gw, gh, niceMin, range } = layout;

  // black zero line if 0 is within the visible range
  if (niceMin < 0 && 0 < range + niceMin) {
    const zeroY = gy + gh - ((0 - niceMin) / range) * gh;
    doc.setDrawColor(128, 128, 128);
    doc.setLineWidth(0.6);
    doc.line(gx, zeroY, gx + gw, zeroY);
  }

  // line
  const [r, g, b] = hexToRgb(COLOR_BLUE);
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(1.2);

  const pts: Array<{ x: number; y: number }> = [];
  const stepX = gw / Math.max(1, years.length - 1);

  for (let i = 0; i < years.length; i++) {
    const v = cumulative[i] ?? 0;
    const py = gy + gh - ((v - niceMin) / range) * gh;
    const px = gx + stepX * i;
    pts.push({ x: px, y: py });
  }

  for (let i = 1; i < pts.length; i++) doc.line(pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y);

  // dots
  for (const p of pts) {
    doc.setFillColor(r, g, b);
    doc.circle(p.x, p.y, 2.2, "F");
  }

  // x labels
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  for (let i = 0; i < years.length; i++) {
    const px = gx + stepX * i;
    doc.text(String(years[i]), px, gy + gh + 12, { align: "center" });
  }

  // x-axis legend
  doc.setFontSize(9);
  doc.text(i18n.t("project.financial_model.year_axis", { defaultValue: "Years" }), gx + gw / 2, gy + gh + 26, { align: "center" });
  
  // re-draw axes on top (for consistency with bar chart)
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.6);
  doc.line(gx, gy, gx, gy + gh);           // Y-axis
  doc.line(gx, gy + gh, gx + gw, gy + gh); // X-axis
}

/* ------------------------------- PDF helpers ------------------------------- */

function addFooterPageNumbers(doc: any) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footer = `- ${i} of ${pageCount} -`;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth(footer);
    doc.text(footer, (pageWidth - textWidth) / 2, doc.internal.pageSize.getHeight() - 18);
  }
}

function drawLogoTopRight(doc: any, y: number, rightMargin = 40) {
  const blue = "#002E66";
  const orange = "#FF6F0F";
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(16);
  const spaceW = doc.getTextWidth(" ");

  doc.setFont("helvetica", "bold");
  doc.setTextColor(blue);
  const wElani = doc.getTextWidth("ELANI");

  doc.setFont("helvetica", "bold");
  doc.setTextColor(orange);
  const wDot = doc.getTextWidth(".");

  doc.setFont("helvetica", "normal");
  doc.setTextColor(blue);
  const wEnergy = doc.getTextWidth("Energy");

  const total = wElani + spaceW + wDot + wEnergy;
  const startX = pageWidth - rightMargin - total;

  doc.setFont("helvetica", "bold");
  doc.setTextColor(blue);
  doc.text("ELANI", startX, y);

  let x = startX + wElani + spaceW;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(orange);
  doc.text(".", x, y);
  x += wDot;

  doc.setFont("helvetica", "normal");
  doc.setTextColor(blue);
  doc.text("Energy", x, y);
}

const PDF_TABLE_FONT_SIZE = 7;
const PDF_TABLE_HEADER_FILL: [number, number, number] = [240, 240, 240];

function pdfTableStyles() {
  return {
    fontSize: PDF_TABLE_FONT_SIZE,
    cellPadding: 2,
    lineWidth: 0,
  };
}

function pdfTableHeadStyles() {
  return {
    fillColor: PDF_TABLE_HEADER_FILL,
    textColor: [0, 0, 0] as [number, number, number],
    fontSize: PDF_TABLE_FONT_SIZE,
  };
}

// equal-width numeric columns (except Year) with smaller Year, and Year left-aligned
function equalWidthColumnStyles(
  doc: any,
  columns: number,
  yearColWidth: number,
  marginLeft: number,
  marginRight: number
) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const available = pageWidth - marginLeft - marginRight;
  const rest = Math.max(available - yearColWidth, 0);
  const per = columns > 1 ? rest / (columns - 1) : rest;

  const styles: Record<number, any> = { 0: { cellWidth: yearColWidth, halign: "left" } };
  for (let i = 1; i < columns; i++) styles[i] = { cellWidth: per, halign: "right" };
  return styles;
}

function weightedColumnStyles(
  doc: any,
  weights: number[],
  marginLeft: number,
  marginRight: number
) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const available = pageWidth - marginLeft - marginRight;
  const total = weights.reduce((sum, w) => sum + w, 0);
  const styles: Record<number, any> = {};
  weights.forEach((w, i) => {
    styles[i] = {
      cellWidth: (available * w) / total,
      halign: i === 0 ? "left" : "right",
    };
  });
  return styles;
}

/* ---------------------------------- PDF ----------------------------------- */

export async function downloadSummaryPdf(
  results: ProjectModelResults | any,
  project: any,
  filename: string,
  opts: { includeKeyFacts: boolean; includeFinancing: boolean; includeCashFlow: boolean; }
) {
  const jsPDF = (await importJsPdf()).default;
  const autoTable = (await importAutoTable()).default;

  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const blue = "#002E66";
  const headerFill = [240, 240, 240] as [number, number, number];
  const marginLeft = 40;
  const marginRight = 40;
  const SECTION_GAP = 46;     // even more spacing between sections on Page 1
  const COL_GUTTER = 24;

  /* ------------------------------- Page 1 -------------------------------- */
  drawLogoTopRight(doc, 40);

  // Title bigger
  doc.setTextColor(blue);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16); // increased
  doc.text(i18n.t("project.export.project_summary"), marginLeft, 64);

  // Project line
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  const projectName = resolveProjectName(results, project);
  doc.text(`${i18n.t("project.header.project_prefix")} ${projectName}`, marginLeft, 92);

  // Status
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`${i18n.t("project.export.status_as_of", { date: todayDottedBerlin() })}`, marginLeft, 112);

  let y = 112 + SECTION_GAP; // more gap before first section header

  // Shared style for P1 "no-box" two-column tables
  const pageW = doc.internal.pageSize.getWidth();
  const usable = pageW - marginLeft - marginRight;
  const colW = (usable - COL_GUTTER) / 2;

  const buildColStyles = (valueLeft: boolean) => {
    const labelW = Math.floor(colW * 0.52);
    const valW = colW - labelW;
    return {
      0: { cellWidth: labelW, halign: "left" },
      1: { cellWidth: valW, halign: valueLeft ? "left" : "right" },
    } as Record<number, any>;
  };

  const p1TableOptsBase = {
    theme: "plain" as const,
    styles: { fontSize: 9, cellPadding: { top: 4, bottom: 4, left: 0, right: 0 }, lineWidth: 0 },
    head: [] as any[],
    showHead: "never" as const,
    tableWidth: colW,
  };

  // Key Facts
  if (opts.includeKeyFacts) {
    doc.setTextColor(blue);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(i18n.t("project.export.key_facts"), marginLeft, y);
    y += 12;

    const facts = resolveKeyFacts(results, project);

    const idxCapacity = facts.findIndex(([k]) => k === i18n.t("project.production.capacity"));
    let leftFacts = idxCapacity > -1 ? facts.slice(0, idxCapacity) : facts.slice(0, Math.ceil(facts.length / 2));
    let rightFacts = idxCapacity > -1 ? facts.slice(idxCapacity) : facts.slice(Math.ceil(facts.length / 2));

    const isTariffRow = (k: string) => {
      const tariffLbls = [
        i18n.t("project.sales.initial_tariff_type"),
        i18n.t("project.sales.initial_tariff"),
        i18n.t("project.sales.initial_tariff_duration"),
      ];
      return tariffLbls.includes(k);
    };

    const tariffRows = leftFacts.filter(([k]) => isTariffRow(k));
    leftFacts = leftFacts.filter(([k]) => !isTariffRow(k));

    const idxRelCosts = rightFacts.findIndex(([k]) => k === i18n.t("project.production.relative_costs"));
    if (idxRelCosts >= 0) {
      rightFacts.splice(idxRelCosts + 1, 0, ["", ""]);
      rightFacts.splice(idxRelCosts + 2, 0, ...tariffRows);
    } else {
      rightFacts.push(...tariffRows);
    }

    (autoTable as any)(doc, {
      ...p1TableOptsBase,
      startY: y,
      margin: { left: marginLeft, right: pageW - (marginLeft + colW) },
      columnStyles: buildColStyles(true),
      body: leftFacts.map(([k, v]) => [k, String(v ?? "")]),
    });
    const leftFinalY = (doc as any).lastAutoTable.finalY;

    const rightX = marginLeft + colW + COL_GUTTER;
    (autoTable as any)(doc, {
      ...p1TableOptsBase,
      startY: y,
      margin: { left: rightX, right: pageW - (rightX + colW) },
      columnStyles: buildColStyles(true),
      body: rightFacts.map(([k, v]) => [k, String(v ?? "")]),
    });
    const rightFinalY = (doc as any).lastAutoTable.finalY;

    y = Math.max(leftFinalY, rightFinalY) + SECTION_GAP;
  }

  // Profitability
  {
    doc.setTextColor(blue);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(i18n.t("project.results.key_metrics"), marginLeft, y);
    y += 12;

    const pf = resolveProfitability(results, project);

    const leftRows: Array<[string, string]> = [
      [i18n.t("project.results.roi_avg_20y"), pf.roi20y != null ? fmtPct(pf.roi20y, 1) : ""],
      [i18n.t("project.results.roi_avg_lifetime"), pf.roiLifetime != null ? fmtPct(pf.roiLifetime, 1) : ""],
      [i18n.t("project.results.payback_period"), pf.paybackYears != null ? `${fmtNum(pf.paybackYears, 1)} ${i18n.t("common.years", { defaultValue: "years" })}` : ""],
    ];
    const rightRows: Array<[string, string]> = [
      [i18n.t("project.results.irr_project_short"), pf.irrProject != null ? fmtPct(pf.irrProject, 1) : ""],
      [i18n.t("project.results.irr_equity_levered_pre_tax_short"), pf.irrEquityPre != null ? fmtPct(pf.irrEquityPre, 1) : ""],
      [i18n.t("project.results.irr_equity_levered_post_tax_short"), pf.irrEquityPost != null ? fmtPct(pf.irrEquityPost, 1) : ""],
    ];

    (autoTable as any)(doc, {
      ...p1TableOptsBase,
      startY: y,
      margin: { left: marginLeft, right: pageW - (marginLeft + colW) },
      columnStyles: buildColStyles(true),
      body: leftRows.map(([k, v]) => [k, v]),
    });
    const leftFinalY = (doc as any).lastAutoTable.finalY;

    const rightX = marginLeft + colW + COL_GUTTER;
    (autoTable as any)(doc, {
      ...p1TableOptsBase,
      startY: y,
      margin: { left: rightX, right: pageW - (rightX + colW) },
      columnStyles: buildColStyles(false),
      body: rightRows.map(([k, v]) => [k, v]),
    });
    const rightFinalY = (doc as any).lastAutoTable.finalY;

    y = Math.max(leftFinalY, rightFinalY) + SECTION_GAP;
  }

  // Investment & Financing
  if (opts.includeFinancing) {
    doc.setTextColor(blue);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(i18n.t("project.results.investment_and_financing"), marginLeft, y);
    y += 12;

    const fin = resolveInvestmentAndFinancing(results, project);
    const labelW = Math.floor((pageW - marginLeft - marginRight) * 0.35);
    const valW = pageW - marginLeft - marginRight - labelW;

    (autoTable as any)(doc, {
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 4, bottom: 4, left: 0, right: 0 }, lineWidth: 0 },
      showHead: "never",
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      tableWidth: pageW - marginLeft - marginRight,
      columnStyles: {
        0: { cellWidth: labelW, halign: "left" },
        1: { cellWidth: valW, halign: "right" },
      },
      body: [
        [i18n.t("project.results.initial_investment"), fin.totalInvestment != null ? `${fmtInt(fin.totalInvestment)} EUR` : ""],
        [i18n.t("project.results.debt_nominal"), fin.nominalDebt != null ? `${fmtInt(fin.nominalDebt)} EUR` : ""],
        ...(fin.disagio != null && fin.disagio > 0
          ? [
              [i18n.t("project.results.disagio"), `${fmtInt(fin.disagio)} EUR`],
              [i18n.t("project.results.loan_proceeds"), fin.loanProceeds != null ? `${fmtInt(fin.loanProceeds)} EUR` : ""],
            ]
          : []),
        [i18n.t("project.results.equity"), fin.equity != null ? `${fmtInt(fin.equity)} EUR` : ""],
        [i18n.t("project.financing.interest_rate"), fin.interestRate != null ? `${fmtPct(fin.interestRate, 1)} p.a.` : ""],
      ],
    });
    y = (doc as any).lastAutoTable.finalY + SECTION_GAP;
  }

  // Tax Savings (first 2 years)
  if (opts.includeCashFlow) {
    doc.setTextColor(blue);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(i18n.t("project.results.tax_savings"), marginLeft, y);
    y += 12;

    const t = resolveTaxSavingsFirst2Years(project, results);
    const labelW = Math.floor((pageW - marginLeft - marginRight) * 0.35);
    const valW = pageW - marginLeft - marginRight - labelW;

    (autoTable as any)(doc, {
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 4, bottom: 4, left: 0, right: 0 }, lineWidth: 0 },
      showHead: "never",
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      tableWidth: pageW - marginLeft - marginRight,
      columnStyles: {
        0: { cellWidth: labelW, halign: "left" },
        1: { cellWidth: valW, halign: "right" },
      },
      body: [
        [i18n.t("project.results.iab"), t.iabTax != null ? `${fmtInt(t.iabTax)} EUR` : ""],
        [i18n.t("project.results.special_depreciation"), t.specialTax2y != null ? `${fmtInt(t.specialTax2y)} EUR` : ""],
        [i18n.t("project.taxes.depreciation"), t.baseTax2y != null ? `${fmtInt(t.baseTax2y)} EUR` : ""],
        [i18n.t("project.results.total"), t.total != null ? `${fmtInt(t.total)} EUR` : ""],
      ],
    });
  }

  // Disclaimer
  {
    const pageH = doc.internal.pageSize.getHeight();
    const disclaimer = i18n.t("project.export.disclaimer");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    const wrapped = doc.splitTextToSize(disclaimer, pageW - marginLeft - marginRight);
    doc.text(wrapped, marginLeft, pageH - 80);
  }

  
  /* -------------------------- Page 2: Cash Flow Charts --------------------- */
  {
    doc.addPage("a4", "portrait");
    drawLogoTopRight(doc, 40);

    // Page title
    const blueRgb = hexToRgb("#002E66");
    doc.setTextColor(blueRgb[0], blueRgb[1], blueRgb[2]);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(i18n.t("charts.cashflow.cash_flow_analysis"), marginLeft, 64);

    const series = extractCashFlowSeries(results);

    // centered & smaller chart box within text frame
    const pageWCharts = doc.internal.pageSize.getWidth();
    const usableW = pageWCharts - marginLeft - marginRight;
    const chartW = Math.floor(usableW * 0.95);          // smaller width
    const chartX = marginLeft + Math.floor((usableW - chartW) / 2);

    // Chart 1 title — bold, larger, extra spacing from page title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(i18n.t("charts.cashflow.cash_flow_analysis_subtitle"), marginLeft, 104);

    // Bar chart (more gap below title)
    drawBarChart(
      doc,
      { x: chartX, y: 124, w: chartW, h: 210 },
      series
    );

    // Extra spacing between charts
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(i18n.t("charts.cashflow.cumulative_cash_flow_subtitle"), marginLeft, 394);

    drawLineChart(
      doc,
      { x: chartX, y: 414, w: chartW, h: 210 },
      series
    );
  }


  /* --------------------------- Page 3: Cash flows --------------------------- */
  doc.addPage("a4", "portrait");
  drawLogoTopRight(doc, 40);
  doc.setTextColor(blue);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(i18n.t("project.export.cash_flow"), marginLeft, 64);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(i18n.t("project.financial_model.all_values_in_eur", { defaultValue: "(in EUR)" }), marginLeft, 78);

  const revenue = arr((results as any)?.revenue) || [];
  const opex = arr((results as any)?.opex) || [];
  const debtSvc = arr((results as any)?.debt_service) || [];
  const profit = arr((results as any)?.profit) || [];
  const invest = arr((results as any)?.investment) || [];
  const debt = arr((results as any)?.debt) || [];
  const netDebt = arr((results as any)?.net_debt) || [];
  const taxes = arr((results as any)?.taxes) || [];
  const cash = arr((results as any)?.cashflow) || [];

  const cfYears = Array.from(
    new Set([
      ...revenue.map((r) => Number(r?.Year)),
      ...opex.map((r) => Number(r?.Year)),
      ...debtSvc.map((r) => Number(r?.Year)),
      ...profit.map((r) => Number(r?.Year)),
      ...invest.map((r) => Number(r?.Year)),
      ...netDebt.map((r) => Number(r?.Year)),
      ...taxes.map((r) => Number(r?.Year)),
      ...cash.map((r) => Number(r?.Year)),
    ])
  ).sort((a, b) => a - b);

  const pickCF = (a: any[], y: number, key: string) => {
    const row = a.find((r) => Number(r?.Year) === y);
    return fmtInt(row?.[key]);
  };

  const cfBody = cfYears.map((yy) => [
    yy,
    pickCF(revenue, yy, "Revenue (EUR)"),
    pickCF(opex, yy, "OPEX (EUR)"),
    pickCF(debtSvc, yy, "Debt Service (EUR)"),
    pickCF(profit, yy, "Profit (EUR)"),
    pickCF(invest, yy, "Investment (EUR)"),
    pickCF(netDebt, yy, "Net Debt (EUR)"),
    pickCF(taxes, yy, "Taxes (EUR)"),
    pickCF(cash, yy, "Cash Flow (EUR)"),
    pickCF(cash, yy, "Cumulative Cash Flow (EUR)"),
  ]);

  {
    const cols = 10;
    const cfColWeights = [1, 1.1, 1.1, 1.35, 1.1, 1.3, 1.4, 1.1, 1.1, 1.4];
    const colStyles = weightedColumnStyles(doc, cfColWeights, marginLeft, marginRight);

    (autoTable as any)(doc, {
      startY: 92,
      head: [[
        i18n.t("project.cashflows.columns.year"), 
        i18n.t("project.cashflows.columns.revenue"),
        i18n.t("project.cashflows.columns.opex"),
        i18n.t("project.cashflows.columns.debt_service"),
        i18n.t("project.cashflows.columns.profit"),
        i18n.t("project.cashflows.columns.investment"),
         i18n.t("project.cashflows.columns.net_debt"),
        i18n.t("project.cashflows.columns.taxes"),
        i18n.t("project.cashflows.columns.cash_flow"),
        i18n.t("project.cashflows.columns.cumulative_cf")
      ]],
      body: cfBody,
      theme: "plain",
      styles: pdfTableStyles(),
      headStyles: pdfTableHeadStyles(),
      margin: { left: marginLeft, right: marginRight },
      columnStyles: colStyles,
      didDrawCell: (data: any) => {
        if (data.section === "body" && data.column.index === cols - 1) {
          const yLine = data.cell.y + data.cell.height;
          const pageW2 = data.doc.internal.pageSize.getWidth();
          const x0 = marginLeft;
          const x1 = pageW2 - marginRight;
          data.doc.setDrawColor(headerFill[0], headerFill[1], headerFill[2]);
          data.doc.setLineWidth(0.5);
          data.doc.line(x0, yLine, x1, yLine);
        }
      },
    });
  }

  /* -------------------------- Page 4: Debt schedule ------------------------- */
  doc.addPage("a4", "portrait");
  drawLogoTopRight(doc, 40);
  doc.setTextColor(blue);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(i18n.t("project.debt.schedule"), marginLeft, 64);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(i18n.t("project.financial_model.all_values_in_eur", { defaultValue: "(in EUR)" }), marginLeft, 78);

  const fin_interest = arr((results as any)?.interest) || [];
  const fin_redemption = arr((results as any)?.redemption) || [];
  const fin_debt_service = arr((results as any)?.debt_service) || [];
  const fin_remaining_debt = arr((results as any)?.remaining_debt) || [];

  const yearsDebt = Array.from(
    new Set([
      ...fin_interest.map((r) => Number(r?.Year)),
      ...fin_redemption.map((r) => Number(r?.Year)),
      ...fin_debt_service.map((r) => Number(r?.Year)),
      ...fin_remaining_debt.map((r) => Number(r?.Year)),
    ])
  ).sort((a, b) => a - b);

  const debtBody = yearsDebt.map((yy) => {
    const pick = (a: any[], key: string) => {
      const row = a.find((r) => Number(r?.Year) === yy);
      return fmtInt(row?.[key]);
    };
    return [
      yy,
      pick(fin_interest, "Interest (EUR)"),
      pick(fin_redemption, "Redemption (EUR)"),
      pick(fin_debt_service, "Debt Service (EUR)"),
      pick(fin_remaining_debt, "Remaining Debt (EUR)"),
    ];
  });

  {
    const cols = 5;
    const colStyles = equalWidthColumnStyles(doc, cols, 34, marginLeft, marginRight);

    (autoTable as any)(doc, {
      startY: 92,
      head: [[
        i18n.t("project.debt.columns.year"),
        i18n.t("project.debt.columns.interest"),
        i18n.t("project.debt.columns.principal"),
        i18n.t("project.debt.columns.debt_service"),
        i18n.t("project.debt.columns.ending_balance")
      ]],
      body: debtBody,
      theme: "plain",
      styles: pdfTableStyles(),
      headStyles: pdfTableHeadStyles(),
      margin: { left: marginLeft, right: marginRight },
      columnStyles: colStyles,
      didDrawCell: (data: any) => {
        if (data.section === "body" && data.column.index === cols - 1) {
          const yLine = data.cell.y + data.cell.height;
          const pageW3 = data.doc.internal.pageSize.getWidth();
          const x0 = marginLeft;
          const x1 = pageW3 - marginRight;
          data.doc.setDrawColor(headerFill[0], headerFill[1], headerFill[2]);
          data.doc.setLineWidth(0.5);
          data.doc.line(x0, yLine, x1, yLine);
        }
      },
    });
  }

  addFooterPageNumbers(doc);
  doc.save(filename);
}





/* ---------------------------------- XLSX ---------------------------------- */

export async function downloadSummaryXlsx(
  results: ProjectModelResults | any,
  project: any,
  filename: string,
  opts: { includeKeyFacts: boolean; includeFinancing: boolean; includeCashFlow: boolean }
) {
  const XLSX = await importXlsx();
  const wb = XLSX.utils.book_new();

  /* ---------- SHEET 1: Project summary (single-column) ---------- */
  {
    const rows: any[][] = [];

    // Title + basic status
    rows.push([i18n.t("project.export.project_summary")]);
    rows.push([``]);
    rows.push([`${i18n.t("project.header.project_prefix")} ${resolveProjectName(results, project)}`]);
    rows.push([i18n.t("project.export.status_as_of", { date: todayDottedBerlin() })]);
    rows.push([``]);

    // Key Facts
    if (opts.includeKeyFacts) {
      rows.push([`[${i18n.t("project.export.key_facts")}]`]);
      const facts = resolveKeyFacts(results, project);
      for (const [k, v] of facts) rows.push([`${k}: ${String(v ?? "")}`]);
      rows.push([``]);
    }

    // Profitability (always useful on summary)
    {
      rows.push([`[${i18n.t("project.results.profitability")}]`]);
      const pf = resolveProfitability(results, project);
      rows.push([`${i18n.t("project.results.roi_avg_20y")}: ${pf.roi20y != null ? fmtPct(pf.roi20y, 1) : ""}`]);
      rows.push([`${i18n.t("project.results.roi_avg_lifetime")}: ${pf.roiLifetime != null ? fmtPct(pf.roiLifetime, 1) : ""}`]);
      rows.push([`${i18n.t("project.results.payback_period")}: ${
        pf.paybackYears != null ? `${fmtNum(pf.paybackYears, 1)} ${i18n.t("common.years")}` : ""
      }`]);
      rows.push([`${i18n.t("project.results.irr_project")}: ${pf.irrProject != null ? fmtPct(pf.irrProject, 1) : ""}`]);
      rows.push([`${i18n.t("project.results.irr_equity_levered_pre_tax")}: ${pf.irrEquityPre != null ? fmtPct(pf.irrEquityPre, 1) : ""}`]);
      rows.push([`${i18n.t("project.results.irr_equity_levered_post_tax")}: ${pf.irrEquityPost != null ? fmtPct(pf.irrEquityPost, 1) : ""}`]);
      rows.push([``]);
    }

    // Investment & Financing
    if (opts.includeFinancing) {
      const fin = resolveInvestmentAndFinancing(results, project);
      rows.push([`[${i18n.t("project.results.investment_and_financing")}]`]);
      rows.push([`${i18n.t("project.results.initial_investment")}: ${fin.totalInvestment != null ? fmtInt(fin.totalInvestment) + " EUR" : ""}`]);
      rows.push([`${i18n.t("project.results.debt_nominal")}: ${fin.nominalDebt != null ? fmtInt(fin.nominalDebt) + " EUR" : ""}`]);
      if (fin.disagio != null && fin.disagio > 0) {
        rows.push([`${i18n.t("project.results.disagio")}: ${fmtInt(fin.disagio)} EUR`]);
        rows.push([`${i18n.t("project.results.loan_proceeds")}: ${fin.loanProceeds != null ? fmtInt(fin.loanProceeds) + " EUR" : ""}`]);
      }
      rows.push([`${i18n.t("project.results.equity")}: ${fin.equity != null ? fmtInt(fin.equity) + " EUR" : ""}`]);
      rows.push([`${i18n.t("project.financing.interest_rate")}: ${fin.interestRate != null ? fmtPct(fin.interestRate, 1) : ""}`]);
      rows.push([``]);
    }

    // Tax Savings (first 2 years)
    if (opts.includeCashFlow) {
      const t = resolveTaxSavingsFirst2Years(project, results);
      rows.push([`[${i18n.t("project.results.tax_savings")}]`]);
      rows.push([`${i18n.t("project.results.iab")}: ${t.iabTax != null ? fmtInt(t.iabTax) + " EUR" : ""}`]);
      rows.push([`${i18n.t("project.results.special_depreciation")}: ${t.specialTax2y != null ? fmtInt(t.specialTax2y) + " EUR" : ""}`]);
      rows.push([`${i18n.t("project.taxes.depreciation")}: ${t.baseTax2y != null ? fmtInt(t.baseTax2y) + " EUR" : ""}`]);
      rows.push([`${i18n.t("project.results.total")}: ${t.total != null ? fmtInt(t.total) + " EUR" : ""}`]);
      rows.push([``]);
    }

    const wsSummary = XLSX.utils.aoa_to_sheet(rows);
    // make column comfortably wide
    wsSummary["!cols"] = [{ wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, i18n.t("project.export.project_summary"));
  }

  /* ---------- SHEET 2: Cash flows ---------- */
  if (opts.includeCashFlow) {
    const revenue = arr((results as any)?.revenue) || [];
    const opex = arr((results as any)?.opex) || [];
    const debtSvc = arr((results as any)?.debt_service) || [];
    const profit = arr((results as any)?.profit) || [];
    const invest = arr((results as any)?.investment) || [];
    const debt = arr((results as any)?.debt) || [];
    const netDebt = arr((results as any)?.net_debt) || [];
    const taxes = arr((results as any)?.taxes) || [];
    const cash = arr((results as any)?.cashflow) || [];

    const years = Array.from(
      new Set([
        ...revenue.map((r) => Number(r?.Year)),
        ...opex.map((r) => Number(r?.Year)),
        ...debtSvc.map((r) => Number(r?.Year)),
        ...profit.map((r) => Number(r?.Year)),
        ...invest.map((r) => Number(r?.Year)),
        ...netDebt.map((r) => Number(r?.Year)),
        ...taxes.map((r) => Number(r?.Year)),
        ...cash.map((r) => Number(r?.Year)),
      ])
    ).sort((a, b) => a - b);

    const pick = (a: any[], y: number, key: string) => {
      const row = a.find((r) => Number(r?.Year) === y);
      return typeof row?.[key] === "number" ? Math.round(row?.[key] as number) : row?.[key] ?? "";
    };

    const aoaCF: any[][] = [[
      i18n.t("project.cashflows.columns.year"),
      i18n.t("project.cashflows.columns.revenue"),
      i18n.t("project.cashflows.columns.opex"),
      i18n.t("project.cashflows.columns.debt_service"),
      i18n.t("project.cashflows.columns.profit"),
      i18n.t("project.cashflows.columns.investment"),
      i18n.t("project.cashflows.columns.net_debt"),
      i18n.t("project.cashflows.columns.taxes"),
      i18n.t("project.cashflows.columns.cash_flow"),
      i18n.t("project.cashflows.columns.cumulative_cf")
    ]];

    for (const y of years) {
      aoaCF.push([
        y,
        pick(revenue, y, "Revenue (EUR)"),
        pick(opex, y, "OPEX (EUR)"),
        pick(debtSvc, y, "Debt Service (EUR)"),
        pick(profit, y, "Profit (EUR)"),
        pick(invest, y, "Investment (EUR)"),
        pick(netDebt, y, "Net Debt (EUR)"),
        pick(taxes, y, "Taxes (EUR)"),
        pick(cash, y, "Cash Flow (EUR)"),
        pick(cash, y, "Cumulative Cash Flow (EUR)"),
      ]);
    }

    const wsCF = XLSX.utils.aoa_to_sheet(aoaCF);
    wsCF["!cols"] = [{ wch: 6 }, ...Array(9).fill({ wch: 14 })];
    XLSX.utils.book_append_sheet(wb, wsCF, i18n.t("project.export.cash_flow"));
  }

  /* ---------- SHEET 3: Debt schedule ---------- */
  if (opts.includeFinancing) {
    const fin_interest = arr((results as any)?.interest) || [];
    const fin_redemption = arr((results as any)?.redemption) || [];
    const fin_debt_service = arr((results as any)?.debt_service) || [];
    const fin_remaining_debt = arr((results as any)?.remaining_debt) || [];

    const yearsDebt = Array.from(
      new Set([
        ...fin_interest.map((r) => Number(r?.Year)),
        ...fin_redemption.map((r) => Number(r?.Year)),
        ...fin_debt_service.map((r) => Number(r?.Year)),
        ...fin_remaining_debt.map((r) => Number(r?.Year)),
      ])
    ).sort((a, b) => a - b);

    const pick = (a: any[], y: number, key: string) => {
      const row = a.find((r) => Number(r?.Year) === y);
      return typeof row?.[key] === "number" ? Math.round(row?.[key] as number) : row?.[key] ?? "";
    };

    const aoaDebt: any[][] = [[
      i18n.t("project.debt.columns.year"),
      i18n.t("project.debt.columns.interest"),
      i18n.t("project.debt.columns.principal"),
      i18n.t("project.debt.columns.debt_service"),
      i18n.t("project.debt.columns.ending_balance")
    ]];

    for (const y of yearsDebt) {
      aoaDebt.push([
        y,
        pick(fin_interest, y, "Interest (EUR)"),
        pick(fin_redemption, y, "Redemption (EUR)"),
        pick(fin_debt_service, y, "Debt Service (EUR)"),
        pick(fin_remaining_debt, y, "Remaining Debt (EUR)"),
      ]);
    }

    const wsDebt = XLSX.utils.aoa_to_sheet(aoaDebt);
    wsDebt["!cols"] = [{ wch: 6 }, ...Array(4).fill({ wch: 16 })];
    XLSX.utils.book_append_sheet(wb, wsDebt, i18n.t("project.export.debt_schedule"));
  }

  XLSX.writeFile(wb, filename);
}